import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getLookupData } from 'modules/api/lookups';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import { getIsReseller } from 'store/publisher/selectors';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { FileList } from 'components/common/types';
import Search from 'lib/Search';
import FrameFileUploadButton from 'components/common/Deal/FrameFileUploadButton/FrameFileUploadButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { RouteFrameCodeResponse, FrameListPanelProps } from './FrameListPanel.types';
import {
  getManuallySelectedFrames,
  updateFileIncludeStatus,
  toggleFrameInFileExclusionList,
} from './FrameListPanel.utils';
import { SearchResultsSection } from './SearchResultsSection';
import { UploadedFilesSection } from './UploadedFilesSection';
import { SelectedFramesSection } from './SelectedFramesSection';

const MAX_FRAME_FILE_UPLOAD = 5;

const FrameListPanel: React.FC<FrameListPanelProps> = ({
  selectedRouteFrameCodes,
  setSelectedRouteFrameCodes,
  uploadedListOfFiles,
  setUploadedListOfFiles,
}) => {
  const isReseller = useSelector(getIsReseller);
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);

  const [fetchedOptions, setFetchedOptions] = useState<RouteFrameCode[]>([]);
  const [searchText, setSearchText] = useState('');
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const onToggleCategory = (category: string): void => {
    if (openCategories.includes(category))
      setOpenCategories(openCategories.filter((openCategory) => openCategory !== category));
    else setOpenCategories([...openCategories, category]);
  };

  const fetchRouteFrameCodes = async (queryString: string): Promise<void> => {
    if (queryString.length < 2) return;

    const requestArguments = { cancelFunctions, lookupURLName: 'routeframecode', query: '', lookupCode: queryString };

    try {
      let response: RouteFrameCodeResponse[];

      if (isReseller) {
        response = (await multiPublisherRequest(environmentId, resellerMediaOwners, getLookupData, {
          ...requestArguments,
        })) as RouteFrameCodeResponse[];
      } else {
        response = await getLookupData(requestArguments);
      }

      const mappedData: RouteFrameCode[] = response.map(({ code, publisher }) => ({
        code,
        name: code,
        mediaOwner: publisher || environmentId,
      }));
      setFetchedOptions(mappedData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    if (!searchText) {
      setFetchedOptions([]);

      return;
    }

    fetchRouteFrameCodes(searchText);
  }, [searchText]);

  const isFrameSelected = (code: string, mediaOwner?: string): boolean => {
    return selectedRouteFrameCodes.some(
      (frame) => frame.code === code && (mediaOwner ? frame.mediaOwner === mediaOwner : true),
    );
  };

  const handleFrameToggle = (frameCode: RouteFrameCode, checked: boolean): void => {
    if (checked) {
      setSelectedRouteFrameCodes([...selectedRouteFrameCodes, { ...frameCode, include: true }]);
    } else {
      setSelectedRouteFrameCodes(
        selectedRouteFrameCodes.filter(
          (frame) => !(frame.code === frameCode.code && frame.mediaOwner === frameCode.mediaOwner),
        ),
      );
    }
  };

  const handleFileToggle = (file: FileList, allFramesIncluded: boolean): void => {
    const updatedFiles = updateFileIncludeStatus(uploadedListOfFiles, file, !allFramesIncluded);
    setUploadedListOfFiles(updatedFiles);
  };

  const handleFrameToggleInFile = (fileName: string, frameCode: string, isCurrentlyExcluded: boolean): void => {
    const updatedFiles = toggleFrameInFileExclusionList(uploadedListOfFiles, fileName, frameCode, isCurrentlyExcluded);
    setUploadedListOfFiles(updatedFiles);
  };

  const handleRemoveFrame = (code: string, mediaOwner?: string): void => {
    setSelectedRouteFrameCodes(
      selectedRouteFrameCodes.filter(
        (frame) => !(frame.code === code && (mediaOwner ? frame.mediaOwner === mediaOwner : true)),
      ),
    );
  };

  const manuallySelectedFrames = useMemo(
    () => getManuallySelectedFrames(selectedRouteFrameCodes, uploadedListOfFiles, environmentId),
    [selectedRouteFrameCodes, uploadedListOfFiles, environmentId],
  );

  return (
    <div className="min-w-62.5 bg-white border border-neutral-200 rounded-md overflow-hidden shadow-dropdownMenu">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200">
        <div className="flex-1 min-w-0">
          <Search
            value={searchText}
            placeholder="Search frame codes"
            onChange={(value: string) => setSearchText(value)}
          />
        </div>
      </div>

      <div className="px-3 py-2 border-b border-neutral-200">
        <FrameFileUploadButton
          listFiles={[]}
          uploadedListOfFiles={uploadedListOfFiles}
          setUploadedListOfFiles={setUploadedListOfFiles}
          theme={SectionButtonTheme.DASHED}
          isFileUploadVisible
          maxFileUploads={MAX_FRAME_FILE_UPLOAD}
        />
      </div>

      <div className="max-h-72 overflow-y-auto">
        <SearchResultsSection
          fetchedOptions={fetchedOptions}
          searchText={searchText}
          isFrameSelected={isFrameSelected}
          handleFrameToggle={handleFrameToggle}
        />

        <UploadedFilesSection
          uploadedListOfFiles={uploadedListOfFiles}
          openCategories={openCategories}
          onToggleCategory={onToggleCategory}
          handleFileToggle={handleFileToggle}
          handleFrameToggleInFile={handleFrameToggleInFile}
        />

        {!fetchedOptions.length && !searchText && (
          <SelectedFramesSection
            manuallySelectedFrames={manuallySelectedFrames}
            handleRemoveFrame={handleRemoveFrame}
          />
        )}
      </div>
    </div>
  );
};

export default FrameListPanel;
